/* eslint-disable react-refresh/only-export-components */
import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/utils/cn'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-tutara-primary text-white shadow hover:bg-tutara-primary-hover active:scale-[0.98] disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none',
        secondary:
          'bg-white text-tutara-secondary border-2 border-tutara-secondary hover:bg-tutara-secondary hover:text-white active:scale-[0.98]',
        accent:
          'bg-tutara-secondary text-white shadow hover:bg-tutara-secondary-light active:scale-[0.98] disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none',
        destructive:
          'bg-red-500 text-white shadow-sm hover:bg-red-600 active:scale-[0.98] disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none',
        outline:
          'border-2 border-tutara-gray bg-white text-tutara-charcoal hover:border-tutara-primary hover:text-tutara-primary active:scale-[0.98] disabled:opacity-50',
        ghost:
          'text-tutara-secondary hover:bg-tutara-primary/10 hover:text-tutara-primary active:scale-[0.98]',
        link:
          'text-tutara-secondary underline-offset-4 hover:underline active:scale-[0.98]',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 py-2',
        lg: 'h-12 px-8',
        icon: 'h-10 w-10',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      fullWidth: false,
    },
  }
)

export interface TutaraButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const TutaraButton = React.forwardRef<HTMLButtonElement, TutaraButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="animate-spin" />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </button>
    )
  }
)

TutaraButton.displayName = 'TutaraButton'

export { TutaraButton, buttonVariants }